import TextButton from 'components/Luxkit/Button/TextButton'
import Heading from 'components/Luxkit/Typography/Heading'
import { replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import ExperienceSearchTileList from '../ExperienceLists/ExperienceSearchTileList'
import { mediaQueryUp } from 'components/utils/breakpoint'
import variables from 'styles/vendor/bootstrap/variables'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import Places from 'constants/places'
import { arrayToObject, sortBy, take } from 'lib/array/arrayUtils'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import { connect } from 'react-redux'
import { useMultiValueToggle } from 'hooks/useToggle'
import LineMapIcon from 'components/Luxkit/Icons/line/LineMapIcon'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'

const CarouselList = styled.div`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(20)};
  display: grid;
  grid-row-gap: ${rem(20)};
  grid-template-areas:
    "title"
    "carousel"
    "actions";

  ${mediaQueryUp.tablet} {
    grid-template-areas:
      "title actions"
      "carousel carousel";
    padding-bottom: ${rem(40)};
  }
`

const CarouselHeading = styled.div`
  grid-area: title;
  display: grid;
  grid-row-gap: ${rem(16)};

`

const CarouselWrapper = styled.div`
  grid-area: carousel;
`

const Actions = styled.div`
  grid-area: actions;
  align-self: end;
  margin-left: auto;
  display: flex;
  gap: ${rem(12)};
`

const layoutContainerPadding = variables.gutterWidth / 2

const FilterChips = styled.div`
  display: flex;
  grid-row: 2;
  gap: ${rem(4)};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /*
    negative margin left/right lets us 'overflow' the layout container to show more items are scrollable
    negiatve margin + padding top/bottom give pixel calculations space to not accidently hide chip borders
  */
  padding: ${rem(4)} ${rem(layoutContainerPadding)} ${rem(4)} ${rem(layoutContainerPadding)};
  margin: -${rem(4)} -${rem(layoutContainerPadding)} -${rem(4)} -${rem(layoutContainerPadding)};

  &::-webkit-scrollbar {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    padding: 0;
    margin: 0;
    scrollbar-width: auto;

    &::-webkit-scrollbar {
      display: initial;
    }
  }
`

interface Props {
  primaryPlace: App.Place;
  title: string;
  parentCategoryCode?: number;
  sortType?: Experiences.SortTypes;
  filterMode: 'place' | 'category' | 'none';
  categories: Array<App.ExperienceItemCategory>;
  searchFacets?: App.ExperienceListFacetsState;
}

function ExperiencesPageSearchCarousel(props: Props) {
  const { primaryPlace, parentCategoryCode = 999, sortType, title, filterMode, categories, searchFacets } = props
  const [selectedPlace, setSelectedPlace] = useState(primaryPlace)
  const [filteredCategories, toggleCategory] = useMultiValueToggle<number>()

  const trackEvent = useTrackExperienceOfferEvent()

  const handleViewMapClick = useCallback(() => {
    trackEvent('VIEW_ON_MAP_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  const handleViewAllClick = useCallback(() => {
    trackEvent('VIEW_ALL_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  useEffect(() => {
    // our 'first place' is often our current location
    // if we changes, we should default back to that.
    // TODO: this is not an obvious solution, improve it
    if (primaryPlace !== selectedPlace) {
      setSelectedPlace(primaryPlace)
    }
    // eslint-disable-next-line
  }, [primaryPlace])

  const primaryCodes = useMemo(() => ([parentCategoryCode]), [parentCategoryCode])
  const primaryList = useExperienceSearchList({
    categoryCodes: primaryCodes,
    sortBy: sortType,
    placeId: primaryPlace.id,
  })

  const listCodes = useMemo(() => {
    if (filterMode === 'category' && filteredCategories.length > 0) {
      return filteredCategories
    }
    return primaryCodes
  }, [filteredCategories, primaryCodes, filterMode])

  const searchUrl = useMemo(() => {
    const query = replaceExperienceQueryString({
      destinationId: selectedPlace.id,
      destinationName: selectedPlace.name.split(',', 1)[0],
      categories: listCodes,
    })
    return `/search/experiences?${query}`
  }, [selectedPlace, listCodes])

  const searchMapUrl = useMemo(() => {
    const query = replaceExperienceQueryString({
      destinationId: selectedPlace.id,
      destinationName: selectedPlace.name.split(',', 1)[0],
      categories: listCodes,
    })
    return `/search/experiences/map?${query}`
  }, [selectedPlace, listCodes])

  const subCategories = useMemo((): Array<App.ExperienceItemCategory> => {
    const categoryFacetsById = arrayToObject(
      searchFacets?.[selectedPlace.id]?.facets?.category ?? [],
      (cat) => cat.categoryId,
    )

    // Need sub categories of the parent category we're showing and the ones that have valid results to show a list of, i.e. more than 3
    const validCategories = categories.filter(cat => cat.parentId === parentCategoryCode && (categoryFacetsById[cat.id]?.count ?? 0) >= 4)
    const sortedCategoriesByCount = sortBy(validCategories, cat => categoryFacetsById[cat.id] ?? 0, 'desc')
    return take(sortedCategoriesByCount, 3)
  }, [searchFacets, selectedPlace.id, categories, parentCategoryCode])

  // We don't want users to see the same set of experiences when they swap to a different place
  // ensure this happens by excluding the main place from the results of other places
  const placeIdToIgnore = selectedPlace.id !== primaryPlace.id ? primaryPlace.id : undefined

  const tracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', `Experiences ${title} Carousel`),
    { ias: { list: 'experiences' }, key: primaryPlace.id },
  )

  // we want to show a minimum of one full row on the experiences page, which is 4
  if (primaryList.ids.length < 4) {
    return null
  }

  const canChangeLocation = filterMode === 'place' && primaryPlace.id !== Places.Anywhere.id
  const canChangeCategories = filterMode === 'category' && subCategories.length > 1
  const hasFilters = canChangeLocation || canChangeCategories

  return (
    <CarouselList>
      <CarouselHeading>
        <Heading variant="heading3">{title}</Heading>
        {hasFilters && <FilterChips>
          {canChangeLocation && [primaryPlace, Places.Anywhere].map(place => <FilterChip
            key={place.id}
            size="medium"
            selected={selectedPlace.id === place.id}
            onClick={() => setSelectedPlace(place)}
          >
            {place === Places.Anywhere ? 'Around the world' : place.name}
          </FilterChip>,
          )}
          {canChangeCategories && subCategories.map(cat => <FilterChip
            key={cat.id}
            size="medium"
            selected={filteredCategories.includes(cat.id)}
            onClick={() => toggleCategory(cat.id)}
          >
            {cat.name}
          </FilterChip>,
          )}
        </FilterChips>}
      </CarouselHeading>
      <CarouselWrapper>
        <OfferListEventsProvider tracking={tracking}>
          <ExperienceSearchTileList
            placeId={selectedPlace.id}
            sortBy={sortType}
            categories={listCodes}
            placeIdToIgnore={placeIdToIgnore}
          />
        </OfferListEventsProvider>
      </CarouselWrapper>
      <Actions>
        <TextButton
          kind="tertiary"
          to={searchMapUrl}
          onClick={handleViewMapClick}
          startIcon={<LineMapIcon />}
        >
          View on map
        </TextButton>
        <TextButton
          kind="tertiary"
          to={searchUrl}
          variant="dark"
          onClick={handleViewAllClick}
        >
          View all
        </TextButton>
      </Actions>
    </CarouselList>
  )
}

function mapStateToProps(state: App.State) {
  return {
    categories: state.experience.experienceCategories,
    searchFacets: state.experience.experienceListFacets,
  }
}

export default connect(mapStateToProps)(ExperiencesPageSearchCarousel)
